import { type z } from 'zod';
import { recordDiagnostic } from './diagnostics';
import { createCorrelationId, createIdempotencyKey } from './ids';
import { ZagruProblem } from './problem';
import { consumeSseBody, type SseEvent } from './sse';
import { buildUrl, combineSignals, type QueryParams } from './url';

export interface RequestOptions<T = unknown> {
  query?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Validates the parsed body; a mismatch throws a ZagruProblem with code `client.invalid_response`. */
  schema?: z.ZodType<T>;
  /** `true` auto-generates a key for POST/PUT/PATCH/DELETE. */
  idempotencyKey?: string | true;
  correlationId?: string;
  timeoutMs?: number;
  parse?: 'json' | 'text' | 'blob' | 'none';
}

export interface SseOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  onEvent: (evt: SseEvent) => void;
  onOpen?: () => void;
}

export interface ApiClient {
  get<T = unknown>(path: string, options?: RequestOptions<T>): Promise<T>;
  post<T = unknown>(path: string, body?: unknown, options?: RequestOptions<T>): Promise<T>;
  put<T = unknown>(path: string, body?: unknown, options?: RequestOptions<T>): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown, options?: RequestOptions<T>): Promise<T>;
  delete<T = unknown>(path: string, options?: RequestOptions<T>): Promise<T>;
  request<T = unknown>(method: string, path: string, body?: unknown, options?: RequestOptions<T>): Promise<T>;
  /** Server-Sent Events over fetch (GET or POST). Resolves when the stream ends; rejects on HTTP/network errors. */
  sse(path: string, options: SseOptions): Promise<void>;
  readonly baseUrl: string;
}

export interface ApiClientOptions {
  baseUrl?: string;
  getAccessToken?: () => Promise<string | null> | string | null;
  onUnauthorized?: (problem: ZagruProblem) => void | Promise<void>;
  fetch?: typeof fetch;
  defaultHeaders?: Record<string, string>;
  /** Headers computed per request (the chosen workspace, W3) — after the defaults, before the call's own. */
  getDynamicHeaders?: () => Record<string, string>;
  onRequest?: (info: { method: string; url: string; correlationId: string }) => void;
  onResponse?: (info: { method: string; url: string; correlationId: string; status: number; durationMs: number }) => void;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
  );
}

function isJsonContent(res: Response): boolean {
  const ct = res.headers.get('content-type') ?? '';
  return /\bjson\b/i.test(ct) || ct.includes('+json');
}

async function readErrorBody(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    if (!text) return undefined;
    if (isJsonContent(res)) {
      try {
        return JSON.parse(text) as unknown;
      } catch {
        return text;
      }
    }
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
}

async function parseBody(res: Response, mode: RequestOptions['parse']): Promise<unknown> {
  if (mode === 'none') return undefined;
  if (mode === 'blob') return res.blob();
  if (mode === 'text') return res.text();
  if (res.status === 204 || res.status === 205 || res.status === 304) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  if (isJsonContent(res)) return JSON.parse(text) as unknown;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Creates a fetch-based API client following the Zagru API conventions:
 * `Accept: application/json, application/problem+json`, JSON bodies, `Authorization: Bearer` when a
 * token is available, `X-Correlation-Id` on every request, optional `Idempotency-Key`, problem+json
 * parsing into {@link ZagruProblem}, zod response validation and query/array serialisation.
 */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? '';
  const fetchImpl: typeof fetch = options.fetch ?? ((...args) => globalThis.fetch(...args));

  async function resolveToken(): Promise<string | null> {
    if (!options.getAccessToken) return null;
    try {
      return (await options.getAccessToken()) ?? null;
    } catch {
      return null;
    }
  }

  function buildHeaders(method: string, body: unknown, opts: RequestOptions<unknown>, correlationId: string, token: string | null): Headers {
    const headers = new Headers();
    headers.set('Accept', 'application/json, application/problem+json');
    for (const [k, v] of Object.entries(options.defaultHeaders ?? {})) headers.set(k, v);
    for (const [k, v] of Object.entries(options.getDynamicHeaders?.() ?? {})) headers.set(k, v);
    if (body !== undefined && !isRawBody(body)) headers.set('Content-Type', 'application/json');
    else if (typeof body === 'string' && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    headers.set('X-Correlation-Id', correlationId);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (opts.idempotencyKey !== undefined && MUTATING.has(method)) {
      headers.set('Idempotency-Key', opts.idempotencyKey === true ? createIdempotencyKey() : opts.idempotencyKey);
    } else if (typeof opts.idempotencyKey === 'string') {
      headers.set('Idempotency-Key', opts.idempotencyKey);
    }
    for (const [k, v] of Object.entries(opts.headers ?? {})) headers.set(k, v);
    return headers;
  }

  async function execute(method: string, url: string, body: unknown, headers: Headers, signal: AbortSignal | undefined, correlationId: string): Promise<Response> {
    options.onRequest?.({ method, url, correlationId });
    const started = now();
    let res: Response;
    try {
      res = await fetchImpl(url, {
        method,
        headers,
        body: body === undefined ? undefined : isRawBody(body) ? body : JSON.stringify(body),
        signal,
        credentials: 'same-origin',
      });
    } catch (err) {
      options.onResponse?.({ method, url, correlationId, status: 0, durationMs: now() - started });
      throw ZagruProblem.network(err, correlationId);
    }
    options.onResponse?.({ method, url, correlationId, status: res.status, durationMs: now() - started });
    return res;
  }

  /** Tags a problem with the request that produced it and records the technical diagnostic. */
  function fail(problem: ZagruProblem, method: string, path: string): ZagruProblem {
    problem.method = method;
    problem.endpoint = path.split('?')[0];
    recordDiagnostic(problem);
    return problem;
  }

  async function request<T>(methodInput: string, path: string, body?: unknown, opts: RequestOptions<T> = {}): Promise<T> {
    const method = methodInput.toUpperCase();
    try {
      return await perform<T>(method, path, body, opts);
    } catch (err) {
      if (err instanceof ZagruProblem && !err.endpoint) throw fail(err, method, path);
      throw err;
    }
  }

  async function perform<T>(method: string, path: string, body: unknown, opts: RequestOptions<T>): Promise<T> {
    const correlationId = opts.correlationId ?? createCorrelationId();
    const url = buildUrl(baseUrl, path, opts.query);
    const signal = combineSignals(opts.signal, opts.timeoutMs);
    const token = await resolveToken();
    const headers = buildHeaders(method, body, opts, correlationId, token);

    let res = await execute(method, url, body, headers, signal, correlationId);

    if (res.status === 401 && options.getAccessToken) {
      // Single retry when the token source yields a fresh token (e.g. after a silent refresh).
      const fresh = await resolveToken();
      if (fresh && fresh !== token) {
        headers.set('Authorization', `Bearer ${fresh}`);
        res = await execute(method, url, body, headers, signal, correlationId);
      }
    }

    if (!res.ok) {
      const errorBody = await readErrorBody(res);
      const problem = ZagruProblem.fromResponse(res, errorBody, correlationId);
      if (problem.isUnauthorized() && options.onUnauthorized) {
        await options.onUnauthorized(problem);
      }
      throw problem;
    }

    let data: unknown;
    try {
      data = await parseBody(res, opts.parse);
    } catch (err) {
      throw new ZagruProblem({
        status: res.status,
        title: 'Invalid response',
        detail: 'The server response could not be read.',
        code: 'client.invalid_response',
        correlationId,
        diagnostics: [{ path: '$', message: err instanceof Error ? err.message : 'Response body could not be parsed', code: 'parse' }],
        raw: err,
      });
    }
    if (opts.schema) {
      const parsed = opts.schema.safeParse(data);
      if (!parsed.success) {
        // The schema issues are for engineers (diagnostics, console, telemetry). They are deliberately not
        // `errors` - those are field errors a form may show - and not `detail`, which a screen may render.
        throw new ZagruProblem({
          status: res.status,
          title: 'Invalid response',
          detail: 'The server response did not have the expected shape.',
          code: 'client.invalid_response',
          correlationId,
          diagnostics: parsed.error.issues.map((i) => ({ path: i.path.join('.') || '$', code: i.code, message: i.message })),
          raw: data,
        });
      }
      return parsed.data;
    }
    return data as T;
  }

  async function sse(path: string, sseOptions: SseOptions): Promise<void> {
    const method = sseOptions.method ?? 'GET';
    const correlationId = createCorrelationId();
    const url = buildUrl(baseUrl, path);
    const token = await resolveToken();
    const headers = buildHeaders(method, sseOptions.body, { headers: sseOptions.headers }, correlationId, token);
    headers.set('Accept', 'text/event-stream');
    const res = await execute(method, url, sseOptions.body, headers, sseOptions.signal, correlationId);
    if (!res.ok) {
      const errorBody = await readErrorBody(res);
      const problem = fail(ZagruProblem.fromResponse(res, errorBody, correlationId), method, path);
      if (problem.isUnauthorized() && options.onUnauthorized) await options.onUnauthorized(problem);
      throw problem;
    }
    sseOptions.onOpen?.();
    try {
      await consumeSseBody(res.body, sseOptions.onEvent, sseOptions.signal);
    } catch (err) {
      if (sseOptions.signal?.aborted) return;
      throw ZagruProblem.network(err, correlationId);
    }
  }

  return {
    baseUrl,
    request,
    get: (path, opts) => request('GET', path, undefined, opts),
    post: (path, body, opts) => request('POST', path, body, opts),
    put: (path, body, opts) => request('PUT', path, body, opts),
    patch: (path, body, opts) => request('PATCH', path, body, opts),
    delete: (path, opts) => request('DELETE', path, undefined, opts),
    sse,
  };
}
